"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";

import * as React from "react";

type TabState = "active" | "inactive";

interface TabData {
  id: string;
  icon?: React.ReactNode;
  avatar?: {
    src?: string;
    fallback?: string;
  };
  title: string;
  subtitle: string;
  isActive?: boolean;
}

interface FrostedTabsProps {
  tabs: TabData[];
  onTabChange?: (tabId: string) => void;
  className?: string;
}

interface TabItemProps extends TabData {
  onClick?: () => void;
  className?: string;
}

// CVA variants for different tab elements
const backgroundVariants = cva(
  "absolute inset-0 backdrop-blur-[20px] transition-all duration-200",
  {
    variants: {
      state: {
        active: "bg-base-background bg-opacity-20",
        inactive: "bg-base-background bg-opacity-20 group-hover:bg-opacity-30",
      },
    },
    defaultVariants: {
      state: "inactive",
    },
  },
);

const borderVariants = cva(
  [
    "col-start-1 row-start-1 pointer-events-none absolute inset-0 rounded-t-2xl transition-all duration-200",
    "before:absolute before:inset-0 before:rounded-t-2xl before:pointer-events-none before:transition-all before:duration-200",
  ],
  {
    variants: {
      state: {
        active: "before:border-2 before:border-white",
        inactive:
          "before:border before:border-white/50 group-hover:before:border-white/75",
      },
    },
    defaultVariants: {
      state: "inactive",
    },
  },
);

const titleVariants = cva(
  "text-white text-base font-suisse font-bold leading-6 text-center mb-1 transition-all duration-200",
  {
    variants: {
      state: {
        active: "",
        inactive: "group-hover:text-white/90",
      },
    },
    defaultVariants: {
      state: "inactive",
    },
  },
);

const subtitleVariants = cva(
  "text-white text-[0.6rem] font-roboto leading-[14px] text-center transition-all duration-200",
  {
    variants: {
      state: {
        active: "opacity-80",
        inactive: "opacity-80 group-hover:opacity-90",
      },
    },
    defaultVariants: {
      state: "inactive",
    },
  },
);

const TabItem = React.forwardRef<HTMLButtonElement, TabItemProps>(
  (
    {
      icon,
      avatar,
      title,
      subtitle,
      isActive = false,
      onClick,
      className,
      ...props
    },
    ref,
  ) => {
    const tabState: TabState = isActive ? "active" : "inactive";

    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        className={cn(
          "group relative flex flex-col items-center justify-start w-full rounded-t-2xl",
          className,
        )}
        {...props}
      >
        {/* Main tab content area with frosted glass effect */}
        <div className="relative w-full">
          {/* Grid layout for layering - icon will be overlaid naturally */}
          <div className="grid grid-cols-1 grid-rows-1">
            {/* Background frost layer */}
            <div className="col-start-1 row-start-1 h-[70px] w-full">
              <div className={backgroundVariants({ state: tabState })} />
            </div>

            {/* Border using pseudo-element with gradient mask */}
            <div
              className={borderVariants({ state: tabState })}
              style={{
                maskImage: `
                     radial-gradient(circle at 50% 0%, transparent 20px, black 20px),
                     linear-gradient(180deg, black 0%, black 15%, transparent 60%)
                   `,
                WebkitMaskImage: `
                     radial-gradient(circle at 50% 0%, transparent 20px, black 20px),
                     linear-gradient(180deg, black 0%, black 15%, transparent 60%)
                   `,
                maskComposite: "intersect",
                WebkitMaskComposite: "intersect",
              }}
            />

            {/* Content layer */}
            <div className="col-start-1 row-start-1 relative flex flex-col items-center justify-center px-4 py-4 pt-6 pb-4 min-h-[70px]">
              {/* Title */}
              <div className={titleVariants({ state: tabState })}>{title}</div>

              {/* Subtitle */}
              <div className={subtitleVariants({ state: tabState })}>
                {subtitle}
              </div>
            </div>

            {/* Icon/Avatar overlay - positioned in grid to naturally stack on top */}
            {icon ? (
              <div className="col-start-1 row-start-1 flex items-center justify-center w-8 h-8 -translate-y-4 mx-auto">
                {icon}
              </div>
            ) : (
              avatar && (
                <Avatar className="col-start-1 row-start-1 w-8 h-8 rounded-full bg-base-muted overflow-hidden -translate-y-4 mx-auto">
                  <AvatarImage src={avatar.src} alt={title} />
                  <AvatarFallback className="bg-base-muted text-white text-sm">
                    {avatar.fallback || title.charAt(0)}
                  </AvatarFallback>
                </Avatar>
              )
            )}
          </div>
        </div>
      </button>
    );
  },
);

const FrostedTabs = React.forwardRef<HTMLDivElement, FrostedTabsProps>(
  ({ tabs, onTabChange, className, ...props }, ref) => {
    const [activeTab, setActiveTab] = React.useState<string>(
      tabs.find((tab) => tab.isActive)?.id || tabs[0]?.id || "",
    );

    const handleTabClick = React.useCallback(
      (tabId: string) => {
        setActiveTab(tabId);
        onTabChange?.(tabId);
      },
      [onTabChange],
    );

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-row items-start justify-start relative w-full isolate",
          className,
        )}
        {...props}
      >
        {tabs.map((tab) => (
          <TabItem
            key={tab.id}
            {...tab}
            isActive={activeTab === tab.id}
            onClick={() => handleTabClick(tab.id)}
            className={"flex-1"}
          />
        ))}
      </div>
    );
  },
);

export { FrostedTabs };
export type { FrostedTabsProps, TabData };
